import React, { useState } from "react";
// import tefData from "../../test/tef.json";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import BarChart from "../../../../components/charts/Bar";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

export default function TefDetails() {
  const history = useHistory();
  const { t } = useTranslation();

  function handlGoBack() {
    history.goBack();
  }

  const dashboardData = useSelector((state) => state.dashboard.jsonData);

  const tefData = dashboardData?.tef_values || [];

  const [selectedThreat, setSelectedThreat] = useState("");

  const tefMinSum = tefData?.reduce((sum, item) => sum + item["min"], 0);
  const tefAvgSum = tefData?.reduce((sum, item) => sum + item["avg"], 0);
  const tefMaxSum = tefData?.reduce((sum, item) => sum + item["max"], 0);
  const tefBaseDescription = "Select a threat to get description";

  const tefDisplayMinValue = selectedThreat
    ? tefData.find((item) => item.threat === selectedThreat)?.["min"] || 0
    : tefMinSum;

  const tefDisplayAvgValue = selectedThreat
    ? tefData.find((item) => item.threat === selectedThreat)?.["avg"] || 0
    : tefAvgSum;

  const tefDisplayMaxValue = selectedThreat
    ? tefData.find((item) => item.threat === selectedThreat)?.["max"] || 0
    : tefMaxSum;

  const tefDisplayDiscriptionValue = selectedThreat
    ? tefData.find((item) => item.threat === selectedThreat)?.[
        "justification"
      ] || ""
    : tefBaseDescription;

  const tefDisplayData = selectedThreat
    ? [tefData.find((item) => item.threat === selectedThreat)].map((item) => ({
        Threat: item.threat,
        min: item["min"],
        avg: item["avg"],
        max: item["max"],
      }))
    : tefData.map((item) => ({
        Threat: item.threat,
        min: item["min"],
        avg: item["avg"],
        max: item["max"],
      }));

  const handleChange = (event) => {
    setSelectedThreat(event.target.value);
  };

  return (
    <>
      <Box sx={{ marginBottom: { xs: "25%" } }}>
        <Button
          sx={{
            background: "#78c3ae",
            borderRadius: 4,
            fontSize: "16px",
            marginLeft: { xs: "8px", sm: "5px" }, // Remove marginLeft on mobile
            alignSelf: "flex-start",

            "&:hover": {
              backgroundColor: "#60dbb9",
            },
          }}
          onClick={handlGoBack}
        >
          <ArrowBackIcon sx={{ color: "white" }} />
        </Button>
        <Typography sx={{ textAlign: "center", fontSize: "40px" }}>
          Threat Event Frequency
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "stretch",
            maxWidth: "100%",
            // margin: "auto",
            mt: { xs: 2, sm: 2 },
            px: { xs: 1, sm: 2 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              gap: { xs: 2, sm: 2 },
              flex: 1,
              width: "100%",
            }}
          >
            {/* Box 1: Threat selection */}
            <Box
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                bgcolor: "background.paper",
                width: {
                  xs: "90%", // Full width on extra small screens
                  sm: "calc(25% - 16px)", // 25% width on small screens
                  md: "calc(20% - 32px)", // Smaller width (20%) on big screens
                },
                flex: 1,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 180,
              }}
            >
              <FormControl fullWidth>
                <InputLabel
                  id="threat-select-label"
                  sx={{ fontSize: { xs: "1rem", sm: "1.2rem" } }}
                >
                  Threat Type
                </InputLabel>
                <Select
                  labelId="threat-select-label"
                  id="threat-select"
                  value={selectedThreat}
                  label="Threat Type"
                  onChange={handleChange}
                  sx={{
                    height: { xs: "52px", sm: "64px" },
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" } }}
                  >
                    <em>All threats</em>
                  </MenuItem>
                  {tefData.map((threat, index) => (
                    <MenuItem
                      key={index}
                      value={threat.threat}
                      sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem" } }}
                    >
                      {t(threat.threat)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Box 2: TEF Min */}
            <Box
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                bgcolor: "background.paper",
                width: {
                  xs: "90%",
                  sm: "calc(25% - 16px)",
                  md: "calc(20% - 32px)", // Smaller width on big screens
                },
                flex: 1,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 180,
                position: "relative",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                {selectedThreat
                  ? `${t(selectedThreat)} TEF Min`
                  : "TEF Min Sum"}
              </Typography>
              <Typography
                variant="h4"
                color="primary"
                sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}
              >
                {tefDisplayMinValue}
              </Typography>
            </Box>

            {/* Box 3: TEF Average */}
            <Box
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                bgcolor: "background.paper",
                width: {
                  xs: "90%",
                  sm: "calc(25% - 16px)",
                  md: "calc(20% - 32px)", // Smaller width on big screens
                },
                flex: 1,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 180,
                position: "relative",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                {selectedThreat
                  ? `${t(selectedThreat)} TEF Average`
                  : "TEF Sum Average"}
              </Typography>
              <Typography
                variant="h4"
                color="primary"
                sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}
              >
                {tefDisplayAvgValue}
              </Typography>
            </Box>

            {/* Box 4: TEF Max */}
            <Box
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                p: { xs: 2, sm: 3 },
                bgcolor: "background.paper",
                width: {
                  xs: "90%",
                  sm: "calc(25% - 16px)",
                  md: "calc(20% - 32px)", // Smaller width on big screens
                },
                flex: 1,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                minHeight: 180,
                position: "relative",
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                {selectedThreat
                  ? `${t(selectedThreat)} TEF Max`
                  : "TEF Sum Max"}
              </Typography>
              <Typography
                variant="h4"
                color="primary"
                sx={{ fontSize: { xs: "1.75rem", sm: "2.125rem" } }}
              >
                {tefDisplayMaxValue}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/*charts*/}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" }, // Stack vertically on mobile, horizontally on larger screens
            justifyContent: "center",
            alignItems: "flex-start", // Center items vertically
            gap: { xs: 2, sm: 4 }, // Add gap between components
            mt: 4, // Add some margin-top for spacing
            px: { xs: 1, sm: 2 }, // Adjust padding for screen size
          }}
        >
          {/* BarChart Box */}
          <Box
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              p: { xs: 2, sm: 3 },
              bgcolor: "background.paper",
              width: { xs: "90%", sm: "45%", md: "40%" }, // Adjust width for responsiveness
              minHeight: "450px",
              height: "auto", // Fixed height with responsiveness    { xs: "450px", sm: "350px", md: "400px" }
              textAlign: "center",
            }}
          >
            <BarChart
              data={tefDisplayData}
              stacked={true}
              title="TEF min, TEF avg and TEF max by Threat"
            />
          </Box>

          {/* Discription Box */}
          <Box
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              p: { xs: 2, sm: 3 },
              bgcolor: "background.paper",
              width: { xs: "90%", sm: "45%", md: "40%" }, // Adjust width for responsiveness
              minHeight: "450px",
              height: "auto", // Fixed height with responsiveness    { xs: "450px", sm: "350px", md: "400px" }
              textAlign: "start",
            }}
          >
            <Typography>{tefDisplayDiscriptionValue}</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}
